import React, { Component } from 'react';
import PopupContainer from '../../../components/PopupContainer';
import Popup from '../../../components/SimplePopup';
import { withRouter } from 'react-router-dom';
import { API } from '../../../requests';
import moment from 'moment';

import ViewRenderer from './views';
import Spinner from '../../../components/Spinner';
import { commonImages, operatorImages } from '../../../assets/images';
import strings from '../../../globalization';
import { connect } from 'react-redux';
import util from '../../../util';
import {
    TRANSACTION_REPORT_TABS,
    GENERATED_REPORTS_PAGE_SIZE,
} from './constants';

const POPUP_CONSTANTS = {
    STANDARD: 'standard',
    SIMPLE_POPUP: 'simple-popup',
    ERROR: 'error',
    SPINNER_POPUP: 'spinner',
    SUCCESS: 'success',
};

class TransactionReport extends Component {
    constructor(props) {
        super(props);
        const { reduxProps } = this.props;
        const {
            login: { loginInfo },
        } = reduxProps;
        const { company, user } = loginInfo;

        const endDate = moment();
        const startDate = moment().subtract(7, 'days');
        const defaultDates = {
            start: startDate.toDate(),
            end: endDate.toDate(),
        };

        this.state = {
            email: '',
            emailError: '',
            company,
            user,
            startDate: startDate.format('MM-DD-YYYY'),
            endDate: endDate.format('MM-DD-YYYY'),
            dates: defaultDates,
            popup: undefined,
            reportData: null,
            showSuccess: false,
            showFailure: false,
            agencyList: [],
            selectedAgencyId: undefined,
            activeTab: TRANSACTION_REPORT_TABS.REPORT,
            totalCount: 0,
            gridApi: null,
        };

        this.pollInterval = null;
    }

    componentWillMount() {
        this.unlisten = this.props.history.listen(() => {
            this.setState({
                popup: undefined,
            });
        });
    }

    componentWillUnmount() {
        this.unlisten();
        this.clearPollInterval();
    }

    componentDidMount() {
        this.setState({
            popup: undefined,
        });

        this.getActiveAgencies();
    }

    clearPollInterval = () => {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
    };

    validateEmail = email => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    getActiveAgencies = () => {
        API.getActiveAgencies()
            .then(response => {
                this.setState({
                    agencyList: response.data || [],
                });
            })
            .catch(error => {
                console.log('Error fetching active agencies:', error);
            });
    };

    getGeneratedReportsRowsAndTotal = data => {
        if (!data) {
            return { rows: [], total: 0 };
        }
        if (Array.isArray(data)) {
            return { rows: data.filter(Boolean), total: data.length };
        }
        const rows = (
            data.reports ||
            data.content ||
            data.records ||
            data.downloads ||
            data.reportDownloads ||
            []
        ).filter(Boolean);
        const total =
            data.totalCount ?? data.totalElements ?? data.count ?? rows.length;
        return { rows, total };
    };

    getGeneratedReportsData = (paramsAPI, page) => {
        API.fetchTransactionReportsDownloads({
            pageNumber: page,
            pageSize: GENERATED_REPORTS_PAGE_SIZE,
        })
            .then(res => {
                const { rows, total } = this.getGeneratedReportsRowsAndTotal(
                    res.data,
                );
                if (paramsAPI) {
                    paramsAPI.successCallback(rows, total);
                }
                this.setState({ totalCount: total });
            })
            .catch(err => {
                if (paramsAPI) {
                    paramsAPI.successCallback([], 0);
                }
                this.showErrorPopup(util.getErrorMessage(err));
            });
    };

    hasDownloadExtension = fileName => /\.(xlsx|zip|csv)$/i.test(fileName || '');

    getDownloadFileName = (downloadUrl, row) => {
        const defaultFileName = `transaction-report-${moment().format(
            'DD-MM-YYYY',
        )}.xlsx`;
        const rowFileName = row?.fileName ?? row?.reportName;
        if (rowFileName) {
            return this.hasDownloadExtension(rowFileName)
                ? rowFileName
                : `${rowFileName}.xlsx`;
        }
        const urlFileName = downloadUrl.split('?')[0].split('/').pop();
        return this.hasDownloadExtension(urlFileName)
            ? urlFileName
            : defaultFileName;
    };

    getDownloadMimeType = fileName => {
        if (/\.zip$/i.test(fileName)) {
            return 'application/zip';
        }
        if (/\.csv$/i.test(fileName)) {
            return 'text/csv';
        }
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    };

    downloadFileFromUrl = async (downloadUrl, row) => {
        if (!downloadUrl) {
            throw new Error('Download link is not available for this report.');
        }

        const fileName = this.getDownloadFileName(downloadUrl, row);
        const fallbackMimeType = this.getDownloadMimeType(fileName);

        const response = await fetch(downloadUrl);
        if (!response.ok) {
            throw new Error('Failed to download report file.');
        }

        const blob = await response.blob();
        const fileBlob =
            blob.type && blob.type !== 'application/octet-stream'
                ? blob
                : new Blob([blob], { type: fallbackMimeType });
        const objectUrl = window.URL.createObjectURL(fileBlob);

        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = fileName;
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();

        window.setTimeout(() => {
            window.URL.revokeObjectURL(objectUrl);
        }, 1000);
    };

    pollTransactionReportDownload = (jobUuid, row) => {
        this.clearPollInterval();
        this.pollInterval = setInterval(() => {
            API.fetchTransactionReportJob(jobUuid)
                .then(res => {
                    if (res?.data?.status === 'COMPLETED') {
                        this.clearPollInterval();
                        this.downloadFileFromUrl(res?.data?.downloadUrl, row)
                            .then(() => {
                                this.setState({ popup: undefined });
                            })
                            .catch(err => {
                                this.setState({ popup: undefined });
                                this.showErrorPopup(
                                    util.getErrorMessage(err) ||
                                        'Failed to download report. Please try again.',
                                );
                            });
                    }
                    if (res?.data?.status === 'FAILED') {
                        this.clearPollInterval();
                        this.setState({ popup: undefined });
                        this.showErrorPopup(
                            res?.data?.error || 'Report generation failed.',
                        );
                    }
                })
                .catch(err => {
                    this.clearPollInterval();
                    this.setState({ popup: undefined });
                    this.showErrorPopup(util.getErrorMessage(err));
                });
        }, 1000);
    };

    isReportDownloadable = row => {
        if (row?.status !== 'COMPLETED' || !row?.downloadUrl) {
            return false;
        }
        const expiresAt = row?.expiresAt;
        if (expiresAt && moment().isAfter(moment(expiresAt))) {
            return false;
        }
        return true;
    };

    handleDownloadReport = row => {
        if (!this.isReportDownloadable(row)) {
            this.showErrorPopup(
                row?.errorMessage ||
                    'Download is available only for completed reports.',
            );
            return;
        }

        this.showSpinner();
        this.downloadFileFromUrl(row.downloadUrl, row)
            .then(() => {
                this.setState({ popup: undefined });
            })
            .catch(err => {
                this.setState({ popup: undefined });
                this.showErrorPopup(
                    util.getErrorMessage(err) ||
                        'Failed to download report. Please try again.',
                );
            });
    };

    showSpinner = () => {
        this.setState({
            popup: {
                type: POPUP_CONSTANTS.SPINNER_POPUP,
            },
        });
    };

    showErrorPopup = errorMessage => {
        this.setState({
            popup: {
                type: POPUP_CONSTANTS.SIMPLE_POPUP,
                message: errorMessage,
                messageStyle: { color: '#E67717' },
                headingImage: commonImages.errorIcon,
                onClose: this.closePopup,
                buttons: [
                    {
                        text: strings.okayPopup,
                        onClick: this.closePopup,
                        outline: true,
                    },
                ],
            },
        });
    };

    handleSendReport = () => {
        const { email, startDate, endDate, selectedAgencyId } = this.state;

        if (!email.trim()) {
            this.setState({ emailError: 'Email is required' });
            return;
        }

        if (!this.validateEmail(email)) {
            this.setState({ emailError: 'Please enter a valid email address' });
            return;
        }

        this.setState({
            emailError: '',
            popup: {
                type: POPUP_CONSTANTS.SPINNER_POPUP,
            },
        });

        API.getTransactionReports(email, startDate, endDate, selectedAgencyId)
            .then(response => {
                console.log('Transaction report response:', response);
                this.setState({
                    reportData: response.data,
                    showSuccess: true,
                    showFailure: false,
                    popup: undefined,
                });
            })
            .catch(error => {
                console.log('Error fetching transaction report:', error);
                this.setState({
                    showSuccess: false,
                    showFailure: true,
                    popup: undefined,
                });
            });
    };

    refreshGeneratedReportsTable = () => {
        const { gridApi } = this.state;
        if (gridApi) {
            const dataSource = {
                getRows: params => {
                    const page = params.endRow / GENERATED_REPORTS_PAGE_SIZE;
                    this.getGeneratedReportsData(params, page - 1);
                },
            };
            gridApi.setDatasource(dataSource);
        }
    };

    onChangeHandler = (action, data) => {
        switch (action) {
            case 'email': {
                this.setState({
                    email: data,
                    emailError: '',
                });
                break;
            }

            case 'agency': {
                this.setState({
                    selectedAgencyId: data,
                });
                break;
            }

            case 'date': {
                this.setState({
                    startDate: data.startDate,
                    endDate: data.endDate,
                    dates: data.dates,
                });
                break;
            }

            case 'gridAPI': {
                this.setState({ gridApi: data }, () => {
                    this.refreshGeneratedReportsTable();
                });
                break;
            }

            case 'updateTable': {
                this.refreshGeneratedReportsTable();
                break;
            }

            default: {
                console.info('case not handled', action);
            }
        }
    };

    onClickHandler = (action, data) => {
        switch (action) {
            case 'sendReport': {
                this.handleSendReport();
                break;
            }

            case 'backToForm': {
                this.setState({
                    showSuccess: false,
                    showFailure: false,
                    email: '',
                    reportData: null,
                });
                break;
            }

            case 'retry': {
                this.setState({
                    showFailure: false,
                });
                break;
            }

            case 'switchTab': {
                this.setState({
                    activeTab: data,
                    showSuccess: false,
                    showFailure: false,
                });
                break;
            }

            case 'downloadReport': {
                this.handleDownloadReport(data);
                break;
            }

            case 'refreshReports': {
                this.refreshGeneratedReportsTable();
                break;
            }

            case 'viewGeneratedReports': {
                this.setState({
                    activeTab: TRANSACTION_REPORT_TABS.GENERATED,
                    showSuccess: false,
                    showFailure: false,
                });
                break;
            }

            default: {
                console.info('case not handled', action);
            }
        }
    };

    closePopup = () => {
        this.setState({
            popup: undefined,
        });
    };

    getProps = () => {
        const { user } = this.state;
        return {
            onClick: this.onClickHandler,
            onChange: this.onChangeHandler,
            email: this.state.email,
            emailError: this.state.emailError,
            dates: this.state.dates,
            roleId: user.roleId,
            showSuccess: this.state.showSuccess,
            showFailure: this.state.showFailure,
            reportData: this.state.reportData,
            agencyList: this.state.agencyList,
            selectedAgencyId: this.state.selectedAgencyId,
            activeTab: this.state.activeTab,
            totalCount: this.state.totalCount,
            pageSize: GENERATED_REPORTS_PAGE_SIZE,
            transactionReportTabs: [
                { label: 'Transaction Report', id: TRANSACTION_REPORT_TABS.REPORT },
                { label: 'Generated Reports', id: TRANSACTION_REPORT_TABS.GENERATED },
            ],
        };
    };

    getPopupContent = () => {
        const { popup } = this.state;
        if (!popup) return null;
        switch (popup.type) {
            case POPUP_CONSTANTS.SIMPLE_POPUP: {
                return <Popup {...popup} />;
            }
            case POPUP_CONSTANTS.SPINNER_POPUP: {
                return <Spinner name="cube-grid" color="#0045E6" {...popup} />;
            }
            default: {
                console.warn('getPopupContent', popup.type);
                return null;
            }
        }
    };

    render() {
        const viewProps = this.getProps();
        const { popup } = this.state;
        return (
            <>
                {popup ? (
                    <PopupContainer> {this.getPopupContent()} </PopupContainer>
                ) : null}
                <ViewRenderer {...viewProps} />
            </>
        );
    }
}

const mapStateToProps = reduxProps => ({
    reduxProps,
});

const mapDispatchToProps = dispatch => ({});

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(TransactionReport),
);
